/**
 * tracking.js — Unified tracking utility for MagicFeedPro LP
 *
 * Reads config from localStorage("magicfeed_tracking") and:
 * - Injects Meta Pixel, GA4, Google Ads, Microsoft UET scripts dynamically
 * - Exports event functions: trackPageView, trackUrlSent, trackEmailSent, trackCtaAudit, trackCtaFinal
 * - All functions are silent (try/catch, skip if not configured)
 */

const LS_TRACKING_KEY = "magicfeed_tracking";

let _config = null;
let _initialized = false;
const _firedEvents = new Set(); // dedup guard: "eventName_key"

function getConfig() {
  if (_config) return _config;
  try {
    _config = JSON.parse(localStorage.getItem(LS_TRACKING_KEY) || "{}");
  } catch {
    _config = {};
  }
  return _config;
}

/** Reset cached config so next getConfig() re-reads from localStorage.
 *  Does NOT reset _initialized — scripts are already in the DOM. */
export function refreshConfig() {
  _config = null;
}

// ── Script injection helpers ──

function injectScript(src, id) {
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
}

function injectInline(code, id) {
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.textContent = code;
  document.head.appendChild(s);
}

// Wait for a global to become available (max ~15s)
function waitForGlobal(name, cb, tries = 60) {
  if (window[name]) return cb(window[name]);
  if (tries <= 0) {
    console.warn(`[MFP Tracking] ${name} never loaded after timeout`);
    return;
  }
  setTimeout(() => waitForGlobal(name, cb, tries - 1), 250);
}

// Dedup helper — returns true if this event was already fired
function alreadyFired(key) {
  if (_firedEvents.has(key)) return true;
  _firedEvents.add(key);
  return false;
}

// ── Initialize all pixels ──

export function initTracking() {
  const cfg = getConfig();

  // Meta Pixel — always try if configured (allows late config arrival)
  if (cfg.metaPixelId && !document.getElementById("mfp-meta-pixel")) {
    try {
      injectInline(`
        !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
        n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}
        (window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
        fbq('init','${cfg.metaPixelId}');
      `, "mfp-meta-pixel");
    } catch {}
  }

  // GA4 — inject if configured and not yet present
  if (cfg.ga4Id && !document.getElementById("mfp-ga4")) {
    try {
      injectScript(`https://www.googletagmanager.com/gtag/js?id=${cfg.ga4Id}`, "mfp-ga4");
      injectInline(`
        window.dataLayer=window.dataLayer||[];
        function gtag(){dataLayer.push(arguments);}
        gtag('js',new Date());
        gtag('config','${cfg.ga4Id}',{send_page_view:false});
      `, "mfp-ga4-config");
    } catch {}
  }

  // Google Ads Remarketing
  if (cfg.googleAdsRemarketingId && !document.getElementById("mfp-gads-rmk")) {
    try {
      injectScript(`https://www.googletagmanager.com/gtag/js?id=${cfg.googleAdsRemarketingId}`, "mfp-gads-rmk");
      injectInline(`
        window.dataLayer=window.dataLayer||[];
        function gtag(){dataLayer.push(arguments);}
        gtag('config','${cfg.googleAdsRemarketingId}',{send_page_view:false});
      `, "mfp-gads-rmk-config");
    } catch {}
  }

  // Microsoft UET
  if (cfg.microsoftUetId && !document.getElementById("mfp-ms-uet")) {
    try {
      injectInline(`
        (function(w,d,t,r,u){var f,n,i;w[u]=w[u]||[],f=function(){var o={ti:"${cfg.microsoftUetId}",enableAutoSpaTracking:true};
        o.q=w[u],w[u]=new UET(o)},n=d.createElement(t),n.src=r,n.async=1,n.onload=n.onreadystatechange=function(){
        var s=this.readyState;s&&s!=="loaded"&&s!=="complete"||(f(),n.onload=n.onreadystatechange=null)},
        i=d.getElementsByTagName(t)[0],i.parentNode.insertBefore(n,i)})(window,document,"script","//bat.bing.com/bat.js","uetq");
      `, "mfp-ms-uet");
    } catch {}
  }

  _initialized = true;
}

// ── Event functions ──

export function trackPageView(pageName) {
  if (alreadyFired(`pageview_${pageName}`)) return;
  const cfg = getConfig();

  // Meta Pixel — wait for fbq to load
  if (cfg.metaPixelId) {
    waitForGlobal("fbq", (fbq) => {
      try { fbq("track", "PageView", { page: pageName }); } catch {}
    });
  }
  // GA4
  if (cfg.ga4Id) {
    waitForGlobal("gtag", (gtag) => {
      try { gtag("event", "page_view", { page_title: pageName }); } catch {}
    });
  }
  // Microsoft UET
  try {
    if (cfg.microsoftUetId && window.uetq) {
      window.uetq.push("event", "page_view", { page_path: pageName });
    }
  } catch {}
}

export function trackUrlSent(storeUrl) {
  if (alreadyFired(`urlsent_${storeUrl}`)) return;
  const cfg = getConfig();

  // Meta Pixel
  if (cfg.metaPixelId) {
    waitForGlobal("fbq", (fbq) => {
      try { fbq("trackCustom", "AuditUrlSent", { store_url: storeUrl }); } catch {}
    });
  }
  // GA4
  if (cfg.ga4Id) {
    waitForGlobal("gtag", (gtag) => {
      try { gtag("event", "audit_url_sent", { store_url: storeUrl }); } catch {}
    });
  }
  // Google Ads conversion — URL Sent
  if (cfg.googleAdsConversionId && cfg.googleAdsLabelUrlSent) {
    waitForGlobal("gtag", (gtag) => {
      try {
        gtag("event", "conversion", {
          send_to: `${cfg.googleAdsConversionId}/${cfg.googleAdsLabelUrlSent}`,
          value: 0, currency: "USD",
        });
      } catch {}
    });
  }
  // Microsoft UET
  try {
    if (cfg.microsoftUetId && window.uetq) {
      window.uetq.push("event", "audit_url_sent", { event_category: "engagement", event_label: storeUrl });
    }
  } catch {}
}

export function trackEmailSent(email, storeUrl, score) {
  if (alreadyFired("emailsent")) return;
  const cfg = getConfig();

  // Unique event_id for Meta deduplication (Pixel + CAPI send the same id)
  const eventId = "emailsent_" + Date.now() + "_" + Math.random().toString(36).slice(2, 10);

  console.log("[MFP Tracking] trackEmailSent called", { hasFbq: !!window.fbq, metaPixelId: cfg.metaPixelId, score, eventId });

  // Meta Pixel — fire "EmailSent" custom event IMMEDIATELY (navigation may happen soon)
  if (cfg.metaPixelId) {
    if (window.fbq) {
      try {
        window.fbq("trackCustom", "EmailSent", { content_name: "audit_email", value: score, store_url: storeUrl }, { eventID: eventId });
        console.log("[MFP Tracking] fbq EmailSent event fired immediately");
      } catch (err) {
        console.warn("[MFP Tracking] fbq EmailSent error:", err);
      }
    } else {
      console.warn("[MFP Tracking] fbq not available yet, waiting...");
      waitForGlobal("fbq", (fbq) => {
        try {
          fbq("trackCustom", "EmailSent", { content_name: "audit_email", value: score, store_url: storeUrl }, { eventID: eventId });
          console.log("[MFP Tracking] fbq EmailSent event fired after wait");
        } catch {}
      });
    }
  }

  // GA4 + Google Ads — fire IMMEDIATELY if gtag available (navigation may happen soon)
  if (cfg.ga4Id || cfg.googleAdsConversionId) {
    const fireGtag = (gtag) => {
      try {
        // GA4
        if (cfg.ga4Id) {
          gtag("event", "generate_lead", {
            currency: "USD",
            value: 0,
            store_url: storeUrl,
            score: score,
          });
          console.log("[MFP Tracking] GA4 generate_lead fired");
        }
        // Google Ads conversion — Email Sent
        if (cfg.googleAdsConversionId && cfg.googleAdsLabelEmailSent) {
          gtag("event", "conversion", {
            send_to: `${cfg.googleAdsConversionId}/${cfg.googleAdsLabelEmailSent}`,
            value: 1,
            currency: "USD",
          });
          console.log("[MFP Tracking] Google Ads conversion fired");
        }
      } catch (err) {
        console.warn("[MFP Tracking] gtag error:", err);
      }
    };
    if (window.gtag) {
      fireGtag(window.gtag);
    } else {
      waitForGlobal("gtag", fireGtag);
    }
  }

  // Microsoft UET
  try {
    if (cfg.microsoftUetId && window.uetq) {
      window.uetq.push("event", "lead", { event_category: "conversion", event_label: storeUrl, event_value: score });
    }
  } catch {}

  // Meta CAPI (server-side — no script dependency)
  if (cfg.metaCapiToken && cfg.metaPixelId) {
    sendCapiEvent({
      event_name: "EmailSent",
      event_id: eventId,
      email,
      store_url: storeUrl,
      score,
      pixel_id: cfg.metaPixelId,
      capi_token: cfg.metaCapiToken,
    });
  }
}

export function trackCtaAudit(storeUrl) {
  if (alreadyFired("ctaaudit")) return;
  const cfg = getConfig();
  try {
    if (cfg.metaPixelId && window.fbq) {
      window.fbq("trackCustom", "CtaAuditClick", { store_url: storeUrl });
    }
    if (cfg.ga4Id && window.gtag) {
      window.gtag("event", "cta_audit_click", { store_url: storeUrl });
    }
    // Google Ads conversion — CTA Audit
    if (cfg.googleAdsConversionId && cfg.googleAdsLabelCtaAudit && window.gtag) {
      window.gtag("event", "conversion", {
        send_to: `${cfg.googleAdsConversionId}/${cfg.googleAdsLabelCtaAudit}`,
        value: 5,
        currency: "USD",
      });
    }
    if (cfg.microsoftUetId && window.uetq) {
      window.uetq.push("event", "cta_audit_click", { event_category: "conversion" });
    }
  } catch {}
}

export function trackCtaFinal() {
  if (alreadyFired("ctafinal")) return;
  const cfg = getConfig();
  try {
    if (cfg.metaPixelId && window.fbq) {
      window.fbq("trackCustom", "CtaFinalClick");
    }
    if (cfg.ga4Id && window.gtag) {
      window.gtag("event", "cta_final_click");
    }
    // Google Ads conversion — CTA Final
    if (cfg.googleAdsConversionId && cfg.googleAdsLabelCtaFinal && window.gtag) {
      window.gtag("event", "conversion", {
        send_to: `${cfg.googleAdsConversionId}/${cfg.googleAdsLabelCtaFinal}`,
        value: 10,
        currency: "USD",
      });
    }
    if (cfg.microsoftUetId && window.uetq) {
      window.uetq.push("event", "cta_final_click", { event_category: "conversion" });
    }
  } catch {}
}

// ── Meta CAPI helper ──

function sendCapiEvent({ event_name, event_id, email, store_url, score, pixel_id, capi_token }) {
  try {
    fetch("/api/capi", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_name,
        event_id,
        email,
        store_url,
        score,
        pixel_id,
        capi_token,
        event_source_url: window.location.href,
        user_agent: navigator.userAgent,
      }),
    }).catch(() => {});
  } catch {}
}
